import React, { useState, useEffect, useRef } from 'react';
import {
    Typography, Card, Button, Space, Form, Input, InputNumber, DatePicker,
    message, Row, Col, Radio, Spin, Modal, Progress, Tag, Alert,
    Upload, Popconfirm, Checkbox, Tooltip, Divider, Switch
} from 'antd';
import dayjs from 'dayjs';
import {
    SaveOutlined, PlusOutlined, DeleteOutlined, BookOutlined,
    PaperClipOutlined, FolderOutlined, FileTextOutlined, AppstoreOutlined,
    ArrowLeftOutlined, LoadingOutlined, EditOutlined, EyeOutlined,
    StopOutlined, DownloadOutlined, InfoCircleOutlined, SendOutlined, LineChartOutlined,
    RightOutlined, DownOutlined, BellOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
    addManualModule, updateManualModule, deleteManualModule,
    uploadFile, deleteFileMetadata, getManualModules, publishMoodleProgram
} from '../services/moodleService';
import { useAuth } from '../hooks/useAuth';
import {
    adminGetTrainingById, adminUpdateTraining,
    adminToggleTrainingStatus, adminDeleteTraining,
    adminSummarizeChanges,
    adminSendProgramUpdateNotification
} from '../services/adminService';
import { TrainingType, NestedModule, NestedSubModule, NestedAssignment } from '../types/training';
import { reconstructFromLiveModules } from '../utils/moduleUtils';
import { defaultEvalCriteria, serializeEvalCriteria } from '../utils/evalCriteria';
import { FIELD_LABELS, SKIP_FIELDS } from '../utils/fieldLabels';
import { detectCommonChanges } from '../utils/changeDetection';

const { Title, Text } = Typography;
const { TextArea } = Input;

const genId = () => Math.random().toString(36).substr(2, 9);

// New assignments start with the standard criteria checklist (editable).
const createEmptyAssignment = (): NestedAssignment => ({
    id: genId(), title: '', description: '', deadline: null, maxCredits: 0, evalCriteria: defaultEvalCriteria()
});
const createEmptySubModule = (): NestedSubModule => ({
    id: genId(), name: '', assignments: [createEmptyAssignment()]
});
const createEmptyModule = (): NestedModule => ({
    id: genId(), name: '', subModules: [], assignments: []
});

const addToSet = (s: Set<string>, ...items: string[]): Set<string> => {
    const n = new Set(Array.from(s));
    items.forEach(i => n.add(i));
    return n;
};

type SelectedNode =
    | { type: 'module'; moduleId: string }
    | { type: 'submodule'; moduleId: string; subId: string }
    | { type: 'assignment'; moduleId: string; subId: string; assignId: string }
    | { type: 'direct_assignment'; moduleId: string; assignId: string };

const EditTrainerLedProgram: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [isActive, setIsActive] = useState(true);
    const [isPublished, setIsPublished] = useState(true);
    const [trainingData, setTrainingData] = useState<any>(null);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const [nestedModules, setNestedModules] = useState<NestedModule[]>([]);
    const originalModulesRef = useRef<NestedModule[]>([]);
    const treeBodyRef = useRef<HTMLDivElement>(null);
    const [pendingFiles, setPendingFiles] = useState<Record<string, any[]>>({});
    const [deletedMoodleIds, setDeletedMoodleIds] = useState<number[]>([]);

    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [progressMessage, setProgressMessage] = useState('');
    const [processStatus, setProcessStatus] = useState<'active' | 'success' | 'exception'>('active');
    const [percent, setPercent] = useState(0);
    const [notifyParticipant, setNotifyParticipant] = useState(true);

    // Two-panel UI state
    const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);
    const [openModuleIds, setOpenModuleIds] = useState<Set<string>>(new Set());
    const [openSubIds, setOpenSubIds] = useState<Set<string>>(new Set());

    const [form] = Form.useForm();
    const navigate = useNavigate();

    useEffect(() => { if (id) fetchData(); }, [id]);

    useEffect(() => {
        if (!selectedNode || !treeBodyRef.current) return;
        const nodeId = selectedNode.type === 'module' ? selectedNode.moduleId
            : selectedNode.type === 'submodule' ? selectedNode.subId
            : (selectedNode as any).assignId;
        const el = treeBodyRef.current.querySelector<HTMLElement>(`[data-node-id="${nodeId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [selectedNode]);

    const fetchData = async () => {
        try {
            setFetching(true);
            const training = await adminGetTrainingById(parseInt(id!));
            setTrainingData(training);
            form.setFieldsValue({
                trainingId: training.trainingId,
                trainingName: training.trainingName,
                trainingType: training.trainingType || TrainingType.PRIVATE,
                discussionEnabled: training.discussionEnabled !== false,
                estimatedEffortHours: training.estimatedEffortHours,
                audienceEmails: training.audienceEmails,
                stakeholderEmails: training.stakeholderEmails,
                trainerEmails: training.trainerEmails,
                trainingLink: training.trainingLink,
                dateRange: training.startDate && training.endDate
                    ? [dayjs(training.startDate.split('T')[0]), dayjs(training.endDate.split('T')[0])]
                    : null
            });
            const isDraft = training.ispublished === false;
            setIsPublished(!isDraft);
            setIsActive(!!training.active || isDraft);

            const courseId = (training as any).courseId || training.id;
            const liveResponse = await getManualModules(courseId);
            const liveModules = liveResponse.modules || [];

            if (liveModules.length > 0) {
                const reconstructed = reconstructFromLiveModules(liveModules);
                const initial = reconstructed.length > 0 ? reconstructed : [createEmptyModule()];
                setNestedModules(initial);
                originalModulesRef.current = initial;
                setOpenModuleIds(new Set());
                setOpenSubIds(new Set());
            } else {
                const initial = [createEmptyModule()];
                setNestedModules(initial);
                originalModulesRef.current = [];
                setOpenModuleIds(new Set());
                setOpenSubIds(new Set());
            }
        } catch (error) {
            console.error('Error fetching program details:', error);
            message.error('Failed to load program details');
        } finally {
            setFetching(false);
        }
    };

    // --- Module CRUD ---
    const addModule = () => {
        const m = createEmptyModule();
        setNestedModules(prev => [...prev, m]);
        setOpenModuleIds(prev => addToSet(prev, m.id));
        setSelectedNode({ type: 'module', moduleId: m.id });
    };
    const removeModule = (moduleId: string) => {
        const mod = nestedModules.find(m => m.id === moduleId);
        if (mod) {
            const ids: number[] = [];
            (mod.assignments || []).forEach(a => { if (a.moodleId) ids.push(a.moodleId); });
            (mod.subModules || []).forEach(sub =>
                (sub.assignments || []).forEach(a => { if (a.moodleId) ids.push(a.moodleId); })
            );
            if (ids.length) setDeletedMoodleIds(prev => [...prev, ...ids]);
        }
        setNestedModules(prev => prev.filter(m => m.id !== moduleId));
        if (selectedNode?.moduleId === moduleId) setSelectedNode(null);
    };
    const updateModuleName = (moduleId: string, name: string) =>
        setNestedModules(prev => prev.map(m => m.id === moduleId ? { ...m, name } : m));

    // --- Sub-Module CRUD ---
    const addSubModule = (moduleId: string) => {
        const s = createEmptySubModule();
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: [...(m.subModules || []), s] } : m));
        setOpenModuleIds(prev => addToSet(prev, moduleId));
        setOpenSubIds(prev => addToSet(prev, s.id));
        setSelectedNode({ type: 'submodule', moduleId, subId: s.id });
    };
    const removeSubModule = (moduleId: string, subId: string) => {
        const mod = nestedModules.find(m => m.id === moduleId);
        const sub = mod?.subModules?.find(s => s.id === subId);
        if (sub) {
            const ids = (sub.assignments || []).filter(a => a.moodleId).map(a => a.moodleId!);
            if (ids.length) setDeletedMoodleIds(prev => [...prev, ...ids]);
        }
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: (m.subModules || []).filter(s => s.id !== subId) } : m));
        if (selectedNode?.type === 'submodule' && selectedNode.subId === subId) setSelectedNode(null);
        if (selectedNode?.type === 'assignment' && selectedNode.subId === subId) setSelectedNode(null);
    };
    const updateSubModuleName = (moduleId: string, subId: string, name: string) =>
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: (m.subModules || []).map(s => s.id === subId ? { ...s, name } : s) } : m));

    // --- Assignment CRUD (under Sub-Module) ---
    const addAssignment = (moduleId: string, subId: string) => {
        const a = createEmptyAssignment();
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: (m.subModules || []).map(s => s.id === subId
                ? { ...s, assignments: [...(s.assignments || []), a] } : s) } : m));
        setOpenModuleIds(prev => addToSet(prev, moduleId));
        setOpenSubIds(prev => addToSet(prev, subId));
        setSelectedNode({ type: 'assignment', moduleId, subId, assignId: a.id });
    };
    const removeAssignment = (moduleId: string, subId: string, assignId: string) => {
        const mod = nestedModules.find(m => m.id === moduleId);
        const sub = mod?.subModules?.find(s => s.id === subId);
        const assign = sub?.assignments?.find(a => a.id === assignId);
        if (assign?.moodleId) setDeletedMoodleIds(prev => [...prev, assign.moodleId!]);
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: (m.subModules || []).map(s => s.id === subId
                ? { ...s, assignments: (s.assignments || []).filter(a => a.id !== assignId) } : s) } : m));
        if (selectedNode?.type === 'assignment' && selectedNode.assignId === assignId) setSelectedNode(null);
    };
    const updateAssignment = (moduleId: string, subId: string, assignId: string, field: keyof NestedAssignment, value: any) =>
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, subModules: (m.subModules || []).map(s => s.id === subId
                ? { ...s, assignments: (s.assignments || []).map(a => a.id === assignId ? { ...a, [field]: value } : a) } : s) } : m));

    // --- Direct Assignment CRUD (under Module) ---
    const addDirectAssignment = (moduleId: string) => {
        const a = createEmptyAssignment();
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, assignments: [...(m.assignments || []), a] } : m));
        setOpenModuleIds(prev => addToSet(prev, moduleId));
        setSelectedNode({ type: 'direct_assignment', moduleId, assignId: a.id });
    };
    const removeDirectAssignment = (moduleId: string, assignId: string) => {
        const mod = nestedModules.find(m => m.id === moduleId);
        const assign = mod?.assignments?.find(a => a.id === assignId);
        if (assign?.moodleId) setDeletedMoodleIds(prev => [...prev, assign.moodleId!]);
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, assignments: (m.assignments || []).filter(a => a.id !== assignId) } : m));
        if (selectedNode?.type === 'direct_assignment' && selectedNode.assignId === assignId) setSelectedNode(null);
    };
    const updateDirectAssignment = (moduleId: string, assignId: string, field: keyof NestedAssignment, value: any) =>
        setNestedModules(prev => prev.map(m => m.id === moduleId
            ? { ...m, assignments: (m.assignments || []).map(a => a.id === assignId ? { ...a, [field]: value } : a) } : m));

    // --- Validation ---
    const validateHierarchy = (): boolean => {
        if (nestedModules.length === 0) { message.error('Please add at least one module.'); return false; }

        const seenModules = new Set<string>();
        for (const mod of nestedModules) {
            if (!mod.name.trim()) { message.error('Please provide names for all modules.'); return false; }

            const hasSubs = (mod.subModules || []).length > 0;
            const hasDirects = (mod.assignments || []).length > 0;
            if (!hasSubs && !hasDirects) {
                message.error(`Add at least one sub-module or direct assignment to "${mod.name}".`);
                return false;
            }

            // Validate sub-modules
            const seenSubs = new Set<string>();
            for (const sub of (mod.subModules || [])) {
                if (!sub.name.trim()) { message.error(`Provide names for all sub-modules in "${mod.name}".`); return false; }
                const subKey = sub.name.trim().toLowerCase();
                if (seenSubs.has(subKey)) {
                    message.error(`Duplicate sub-module "${sub.name}" in "${mod.name}". Sub-module names must be unique within a module.`);
                    return false;
                }
                seenSubs.add(subKey);
                if (!sub.assignments?.length) { message.error(`Add at least one assignment to "${mod.name} > ${sub.name}".`); return false; }
                const seenAssigns = new Set<string>();
                for (const a of sub.assignments) {
                    if (!a.title.trim()) { message.error(`Provide titles for all assignments in "${mod.name} > ${sub.name}".`); return false; }
                    if (!a.description.trim()) { message.error(`Provide a description for "${a.title}".`); return false; }
                    if (a.workflowEnabled && !a.deadline) { message.error(`Set a proposal deadline for "${a.title}".`); return false; }
                    if (!a.stateDeadlines?.completed) { message.error(`Set a final deadline for "${a.title}".`); return false; }
                    const aKey = a.title.trim().toLowerCase();
                    if (seenAssigns.has(aKey)) {
                        message.error(`Duplicate assignment "${a.title}" in "${mod.name} > ${sub.name}".`);
                        return false;
                    }
                    seenAssigns.add(aKey);
                }
            }

            // Validate direct assignments
            const seenDirects = new Set<string>();
            for (const a of (mod.assignments || [])) {
                if (!a.title.trim()) { message.error(`Provide titles for all direct assignments in "${mod.name}".`); return false; }
                if (!a.description.trim()) { message.error(`Provide a description for "${a.title}".`); return false; }
                if (a.workflowEnabled && !a.deadline) { message.error(`Set a proposal deadline for "${a.title}".`); return false; }
                if (!a.stateDeadlines?.completed) { message.error(`Set a final deadline for "${a.title}".`); return false; }
                const aKey = a.title.trim().toLowerCase();
                if (seenDirects.has(aKey)) {
                    message.error(`Duplicate assignment "${a.title}" in "${mod.name}". Assignment titles must be unique within a module.`);
                    return false;
                }
                seenDirects.add(aKey);
            }

            // Unique module names
            const modKey = mod.name.trim().toLowerCase();
            if (seenModules.has(modKey)) {
                message.error(`Duplicate module name "${mod.name}". Each module must have a unique name.`);
                return false;
            }
            seenModules.add(modKey);
        }
        return true;
    };

    const handleDeleteFile = async (fileId: number, assignmentId?: number | string) => {
        try {
            await deleteFileMetadata(fileId);
            message.success('File removed successfully');
            // Removing a reference document invalidates any cached, AI-extracted
            // question text for this assignment - the next trainer to open the
            // eval modal re-extracts from whatever reference files remain.
            if (assignmentId) {
                updateManualModule({ assignmentid: assignmentId, extractedquestions: '' }).catch(() => {});
            }
            fetchData();
        } catch { message.error('Failed to remove file'); }
    };

    // --- Submit ---
    const onFinish = async (values: any) => {
        if (!validateHierarchy()) return;
        try {
            setLoading(true);
            setProgressModalVisible(true);
            setProcessStatus('active');
            setPercent(5);
            setProgressMessage('Updating program metadata...');

            const collectAssignments = (mods: NestedModule[]) => {
                const all: any[] = [];
                for (const mod of mods) {
                    for (const a of mod.assignments || []) all.push(a);
                    for (const sub of mod.subModules || []) {
                        for (const a of sub.assignments || []) all.push(a);
                    }
                }
                return all;
            };
            const allCurrent = collectAssignments(nestedModules);
            const allOriginal = collectAssignments(originalModulesRef.current);
            const newAssignments = allCurrent.filter((a: any) => (!a.moodleId || a.moodleId === 0) && a.title?.trim());

            const originalMap = new Map<number, any>();
            for (const a of allOriginal) {
                if (a.moodleId) originalMap.set(a.moodleId, a);
            }

            const truncate = (s: string, n = 30) => s.length > n ? s.substring(0, n - 1) + '…' : s;
            const normalize = (v: any) =>
                v && typeof v === 'object' && typeof v.unix === 'function'
                    ? String(v.unix())
                    : JSON.stringify(v ?? null);

            const updatedSummaries: string[] = [];
            for (const a of allCurrent) {
                if (!a.moodleId) continue;
                const orig = originalMap.get(a.moodleId);
                if (!orig) continue;
                const allKeys = Array.from(new Set([...Object.keys(a), ...Object.keys(orig)]));
                const changedFields: string[] = [];
                for (const key of allKeys) {
                    if (SKIP_FIELDS.has(key)) continue;
                    if (normalize(a[key]) !== normalize(orig[key])) {
                        changedFields.push(FIELD_LABELS[key] || key);
                    }
                }
                if (changedFields.length > 0) {
                    updatedSummaries.push(`${truncate(a.title || 'Untitled')} (${changedFields.join(', ')})`);
                }
            }

            const changeDescriptions = detectCommonChanges(trainingData, {
                trainingName: values.trainingName,
                startDate: values.dateRange ? values.dateRange[0].format('YYYY-MM-DD') : '',
                endDate: values.dateRange ? values.dateRange[1].format('YYYY-MM-DD') : '',
                trainingType: values.trainingType,
                trainingLink: values.trainingLink || '',
                estimatedEffortHours: values.estimatedEffortHours || 0
            });
            if (deletedMoodleIds.length > 0) {
                changeDescriptions.push({ field: 'Assignments Removed', value: String(deletedMoodleIds.length) });
            }
            for (const summary of updatedSummaries) {
                changeDescriptions.push({ field: 'Assignment Updated', value: summary });
            }

            await adminUpdateTraining(parseInt(id!), {
                id: parseInt(id!),
                trainingName: values.trainingName,
                trainingType: values.trainingType,
                passingPercentage: 100,
                estimatedEffortHours: values.estimatedEffortHours || 0,
                discussionEnabled: values.discussionEnabled !== false,
                audienceEmails: values.audienceEmails || '',
                stakeholderEmails: values.stakeholderEmails || '',
                trainerEmails: values.trainerEmails || '',
                trainingLink: values.trainingLink || '',
                startDate: values.dateRange ? values.dateRange[0].format('YYYY-MM-DD') : '',
                endDate: values.dateRange ? values.dateRange[1].format('YYYY-MM-DD') : '',
                programType: 'TrainerLed',
            });
            const actualNewAssignmentTitles: string[] = [];
            setPercent(15);

            if (deletedMoodleIds.length > 0) {
                setProgressMessage(`Removing ${deletedMoodleIds.length} deleted assignments...`);
                for (const moodleId of deletedMoodleIds.filter(id => !isNaN(id) && id > 0)) {
                    try { await deleteManualModule(moodleId); } catch (e) { console.error(`Failed to delete ${moodleId}`, e); }
                }
                setDeletedMoodleIds([]);
            }
            setPercent(25);

            let totalWork = 0;
            for (const mod of nestedModules) {
                const structure = mod.structureType || 'structured';
                if (structure === 'direct' || structure === 'mixed') totalWork += (mod.assignments || []).length;
                if (structure === 'structured' || structure === 'mixed')
                    (mod.subModules || []).forEach(sub => { totalWork += (sub.assignments || []).length; });
            }
            let done = 0;
            const updateProgress = () => setPercent(25 + Math.round((done / Math.max(totalWork, 1)) * 65));

            const jsonMap: NestedModule[] = JSON.parse(JSON.stringify(nestedModules));

            let sectionNum = 1;
            for (const mod of jsonMap) {

                if (mod.assignments?.length) {
                    for (const assign of mod.assignments) {
                        if (assign.moodleId) {
                            setProgressMessage('Saving assignment changes...');
                            await updateManualModule({
                                assignmentid: assign.moodleId,
                                title: assign.title,
                                content: assign.description,
                                deadline: assign.deadline ? dayjs(assign.deadline).unix() : 0,
                                grade: assign.maxCredits ?? 0,
                                sectionname: `${mod.name}::__direct__`,
                                statedeadlines: assign.stateDeadlines ? JSON.stringify(assign.stateDeadlines) : '',
                                workflowenabled: assign.workflowEnabled ? 1 : 0,
                                evalcriteria: serializeEvalCriteria(assign.evalCriteria),
                            });
                        } else {
                            setProgressMessage(`Creating new assignment: ${assign.title}...`);
                            const result = await addManualModule({
                                courseid: parseInt(id!),
                                title: assign.title,
                                content: assign.description,
                                deadline: assign.deadline ? dayjs(assign.deadline).unix() : 0,
                                sectionnum: sectionNum,
                                grade: assign.maxCredits ?? 0,
                                sectionname: `${mod.name}::__direct__`,
                                statedeadlines: assign.stateDeadlines ? JSON.stringify(assign.stateDeadlines) : '',
                                workflowenabled: assign.workflowEnabled ? 1 : 0,
                                evalcriteria: serializeEvalCriteria(assign.evalCriteria),
                            });
                            assign.moodleId = result.id; actualNewAssignmentTitles.push(assign.title);
                        }
                        const newFiles = pendingFiles[assign.id] || [];
                        for (let fi = 0; fi < newFiles.length; fi++) {
                            const file = newFiles[fi];
                            const fileObj = file.originFileObj || file;
                            setProgressMessage(`Uploading new file: ${fileObj.name} (${fi + 1}/${newFiles.length})`);
                            await new Promise<void>((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = async () => {
                                    try {
                                        const base64 = (reader.result as string || '').split(',')[1] || '';
                                        await uploadFile({ fileName: fileObj.name, fileContent: base64, assignmentid: assign.moodleId!, userid: user?.username || user?.email, filearea: 'intro', filetype: fileObj.type, filesize: fileObj.size, trainingid: trainingData?.trainingId || String(trainingData?.courseId || trainingData?.id || '') });
                                        resolve();
                                    } catch (err) { reject(err); }
                                };
                                reader.onerror = () => reject(new Error('Failed to read file'));
                                reader.readAsDataURL(fileObj);
                            });
                        }
                        if (newFiles.length > 0) {
                            // A new reference file invalidates any cached, AI-extracted question
                            // text - the next trainer to open the eval modal re-extracts fresh.
                            updateManualModule({ assignmentid: assign.moodleId!, extractedquestions: '' }).catch(() => {});
                        }
                        assign.deadline = assign.deadline ? dayjs(assign.deadline).unix() : 0;
                        done++; updateProgress();
                    }
                    sectionNum++;
                }

                if (mod.subModules?.length) {
                    for (const sub of mod.subModules) {
                        for (const assign of sub.assignments) {
                            if (assign.moodleId) {
                                setProgressMessage('Saving assignment changes...');
                                await updateManualModule({
                                    assignmentid: assign.moodleId,
                                    title: assign.title,
                                    content: assign.description,
                                    deadline: assign.deadline ? dayjs(assign.deadline).unix() : 0,
                                    grade: assign.maxCredits ?? 0,
                                    sectionname: `${mod.name}::${sub.name}`,
                                    statedeadlines: assign.stateDeadlines ? JSON.stringify(assign.stateDeadlines) : '',
                                    workflowenabled: assign.workflowEnabled ? 1 : 0,
                                    evalcriteria: serializeEvalCriteria(assign.evalCriteria),
                                });
                            } else {
                                setProgressMessage(`Creating new assignment: ${assign.title}...`);
                                const result = await addManualModule({
                                    courseid: parseInt(id!),
                                    title: assign.title,
                                    content: assign.description,
                                    deadline: assign.deadline ? dayjs(assign.deadline).unix() : 0,
                                    sectionnum: sectionNum,
                                    grade: assign.maxCredits ?? 0,
                                    sectionname: `${mod.name}::${sub.name}`,
                                    statedeadlines: assign.stateDeadlines ? JSON.stringify(assign.stateDeadlines) : '',
                                    workflowenabled: assign.workflowEnabled ? 1 : 0,
                                    evalcriteria: serializeEvalCriteria(assign.evalCriteria),
                                });
                                assign.moodleId = result.id; actualNewAssignmentTitles.push(assign.title);
                            }
                            const newFiles = pendingFiles[assign.id] || [];
                            for (let fi = 0; fi < newFiles.length; fi++) {
                                const file = newFiles[fi];
                                const fileObj = file.originFileObj || file;
                                setProgressMessage(`Uploading new file: ${fileObj.name} (${fi + 1}/${newFiles.length})`);
                                await new Promise<void>((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.onload = async () => {
                                        try {
                                            const base64 = (reader.result as string || '').split(',')[1] || '';
                                            await uploadFile({ fileName: fileObj.name, fileContent: base64, assignmentid: assign.moodleId!, userid: user?.username || user?.email, filearea: 'intro', filetype: fileObj.type, filesize: fileObj.size, trainingid: trainingData?.trainingId || String(trainingData?.courseId || trainingData?.id || '') });
                                            resolve();
                                        } catch (err) { reject(err); }
                                    };
                                    reader.onerror = () => reject(new Error('Failed to read file'));
                                    reader.readAsDataURL(fileObj);
                                });
                            }
                            if (newFiles.length > 0) {
                                // A new reference file invalidates any cached, AI-extracted question
                                // text - the next trainer to open the eval modal re-extracts fresh.
                                updateManualModule({ assignmentid: assign.moodleId!, extractedquestions: '' }).catch(() => {});
                            }
                            assign.deadline = assign.deadline ? dayjs(assign.deadline).unix() : 0;
                            done++; updateProgress();
                        }
                        sectionNum++;
                    }
                }
            }

            setPercent(100);
            for (const title of actualNewAssignmentTitles) {
                changeDescriptions.push({ field: 'Assignment Added', value: truncate(title) });
            }
            if (notifyParticipant && changeDescriptions.length > 0) {
                const changeSummary = await adminSummarizeChanges(changeDescriptions);
                if (changeSummary) await adminSendProgramUpdateNotification(parseInt(id!), changeSummary, user?.name || user?.username || '');
            }

            setProcessStatus('success');
            setProgressMessage('Program updated successfully!');
            setPendingFiles({});
            setTimeout(() => {
                setProgressModalVisible(false);
                fetchData();
            }, 1500);
        } catch (error: any) {
            console.error('Error updating trainer-led program:', error);
            setProcessStatus('exception');
            setProgressMessage(`Error: ${error.message || error}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseProgram = () => {
        const trainingName = form.getFieldValue('trainingName');
        Modal.confirm({
            title: 'Close this program?',
            icon: <StopOutlined style={{ color: '#ff4d4f' }} />,
            content: (
                <div>
                    <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff1f0', borderRadius: 8, border: '1px solid #ffa39e' }}>
                        <div style={{ fontWeight: 'bold' }}>{trainingName}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>ID: {id}</div>
                    </div>
                    <p>Once closed, participants will see it as "Closed" and it becomes read-only.</p>
                </div>
            ),
            okText: 'Yes, Close Program', okType: 'danger', cancelText: 'No, Keep Active',
            onOk: async () => {
                try {
                    setLoading(true);
                    await adminToggleTrainingStatus(parseInt(id!), true);
                    message.success('Program closed successfully');
                    await fetchData();
                } catch { message.error('Failed to close program'); }
                finally { setLoading(false); }
            }
        });
    };

    const handlePublish = async () => {
        if (!trainingData) return;
        try {
            setLoading(true);
            const courseId = (trainingData as any).courseId || trainingData.id;
            await publishMoodleProgram(courseId);
            setIsPublished(true);
            message.success('Program published! Participants can now access it.');
        } catch { message.error('Failed to publish program'); }
        finally { setLoading(false); }
    };

    const handleDeleteTraining = async () => {
        if (!trainingData) return;
        if (deleteConfirmationText.trim() !== trainingData.trainingName.trim()) {
            message.error('Program name does not match'); return;
        }
        try {
            setLoading(true);
            await adminDeleteTraining(trainingData.id, trainingData.trainingId);
            message.success('Program deleted successfully');
            setIsDeleteModalVisible(false);
            navigate('/admin');
        } catch { message.error('Failed to delete program'); }
        finally { setLoading(false); }
    };

    // ── Tree helpers ──────────────────────────────────────────────────────────

    const toggleModule = (id: string) =>
        setOpenModuleIds(prev => { const n = new Set(Array.from(prev)); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleSub = (id: string) =>
        setOpenSubIds(prev => { const n = new Set(Array.from(prev)); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const treeNodeStyle = (selected: boolean, accent: string): React.CSSProperties => ({
        display: 'flex',
        alignItems: 'center',
        padding: '5px 8px',
        borderRadius: 6,
        marginBottom: 2,
        cursor: 'pointer',
        background: selected ? accent + '22' : 'transparent',
        border: `1px solid ${selected ? accent : 'transparent'}`,
        transition: 'background 0.15s',
    });

    const renderAssignmentRow = (
        assign: NestedAssignment,
        idx: number,
        nodeType: 'assignment' | 'direct_assignment',
        moduleId: string,
        subId?: string,
        indent = 32
    ) => {
        const isSelected =
            nodeType === 'assignment'
                ? selectedNode?.type === 'assignment' && selectedNode.assignId === assign.id
                : selectedNode?.type === 'direct_assignment' && selectedNode.assignId === assign.id;
        const handleSelect = () =>
            nodeType === 'direct_assignment'
                ? setSelectedNode({ type: 'direct_assignment', moduleId, assignId: assign.id })
                : setSelectedNode({ type: 'assignment', moduleId, subId: subId!, assignId: assign.id });
        const handleRemove = () =>
            nodeType === 'direct_assignment'
                ? removeDirectAssignment(moduleId, assign.id)
                : removeAssignment(moduleId, subId!, assign.id);

        return (
            <div key={assign.id} data-node-id={assign.id} style={{ paddingLeft: indent }}>
                <div style={treeNodeStyle(isSelected, '#fa8c16')}>
                    <span style={{ width: 16 }} />
                    <div onClick={handleSelect} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', minWidth: 0 }}>
                        <FileTextOutlined style={{ color: '#faad14', fontSize: 11, flexShrink: 0 }} />
                        <Text style={{ fontSize: 12, fontWeight: isSelected ? 600 : 400, color: isSelected ? '#d46b08' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {assign.title || `Assignment ${idx + 1}`}
                        </Text>
                    </div>
                    {isActive && (
                        <Popconfirm title="Remove this assignment?" onConfirm={handleRemove} okText="Remove" okType="danger" cancelText="Cancel">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />}
                                style={{ flexShrink: 0, opacity: 0.5 }} onClick={e => e.stopPropagation()} />
                        </Popconfirm>
                    )}
                </div>
            </div>
        );
    };

    const renderSubModuleRow = (mod: NestedModule, sub: NestedSubModule, subIdx: number) => {
        const isSelected = selectedNode?.type === 'submodule' && selectedNode.subId === sub.id;
        const isOpen = openSubIds.has(sub.id);
        const count = (sub.assignments || []).length;
        return (
            <div key={sub.id} data-node-id={sub.id} style={{ paddingLeft: 16 }}>
                <div style={treeNodeStyle(isSelected, '#52c41a')}>
                    <span
                        onClick={e => { e.stopPropagation(); toggleSub(sub.id); }}
                        style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#8c8c8c' }}
                    >
                        {isOpen ? <DownOutlined style={{ fontSize: 9 }} /> : <RightOutlined style={{ fontSize: 9 }} />}
                    </span>
                    <div
                        onClick={() => setSelectedNode({ type: 'submodule', moduleId: mod.id, subId: sub.id })}
                        style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', minWidth: 0 }}
                    >
                        <FolderOutlined style={{ color: '#52c41a', fontSize: 11, flexShrink: 0 }} />
                        <Text style={{ fontSize: 12, fontWeight: isSelected ? 600 : 400, color: isSelected ? '#389e0d' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            SM{subIdx + 1}: {sub.name || 'Untitled'}
                        </Text>
                        <Tag color="green" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', flexShrink: 0 }}>{count}</Tag>
                    </div>
                    {isActive && (
                        <Space size={0} onClick={e => e.stopPropagation()}>
                            <Tooltip title="Add Assignment">
                                <Button type="text" size="small" icon={<PlusOutlined />} style={{ color: '#52c41a', opacity: 0.8 }}
                                    onClick={() => addAssignment(mod.id, sub.id)} />
                            </Tooltip>
                            <Popconfirm title="Remove this sub-module and all its assignments?" onConfirm={() => removeSubModule(mod.id, sub.id)} okText="Remove" okType="danger" cancelText="Cancel">
                                <Button type="text" size="small" danger icon={<DeleteOutlined />} style={{ opacity: 0.5 }} />
                            </Popconfirm>
                        </Space>
                    )}
                </div>
                {isOpen && (sub.assignments || []).map((a, ai) =>
                    renderAssignmentRow(a, ai, 'assignment', mod.id, sub.id, 16)
                )}
            </div>
        );
    };

    const renderModuleRow = (mod: NestedModule, modIdx: number) => {
        const isSelected = selectedNode?.type === 'module' && selectedNode.moduleId === mod.id;
        const isOpen = openModuleIds.has(mod.id);
        const totalAssignments =
            (mod.assignments || []).length +
            (mod.subModules || []).reduce((s, sub) => s + (sub.assignments || []).length, 0);

        return (
            <div key={mod.id} data-node-id={mod.id} style={{ marginBottom: 4 }}>
                <div style={treeNodeStyle(isSelected, '#1890ff')}>
                    <span
                        onClick={e => { e.stopPropagation(); toggleModule(mod.id); }}
                        style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#8c8c8c' }}
                    >
                        {isOpen ? <DownOutlined style={{ fontSize: 9 }} /> : <RightOutlined style={{ fontSize: 9 }} />}
                    </span>
                    <div
                        onClick={() => setSelectedNode({ type: 'module', moduleId: mod.id })}
                        style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', minWidth: 0 }}
                    >
                        <AppstoreOutlined style={{ color: '#1890ff', fontSize: 11, flexShrink: 0 }} />
                        <Text strong style={{ fontSize: 12, color: isSelected ? '#096dd9' : '#1890ff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            M{modIdx + 1}: {mod.name || 'Untitled Module'}
                        </Text>
                        <Tag color="blue" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', flexShrink: 0 }}>{totalAssignments}</Tag>
                    </div>
                    {isActive && (
                        <Space size={0} onClick={e => e.stopPropagation()}>
                            <Tooltip title="Add Sub-Module">
                                <Button type="text" size="small" icon={<FolderOutlined />} style={{ color: '#52c41a', opacity: 0.8 }}
                                    onClick={() => addSubModule(mod.id)} />
                            </Tooltip>
                            <Tooltip title="Add Direct Assignment">
                                <Button type="text" size="small" icon={<FileTextOutlined />} style={{ color: '#faad14', opacity: 0.8 }}
                                    onClick={() => addDirectAssignment(mod.id)} />
                            </Tooltip>
                            <Popconfirm title="Remove this module and all its content?" onConfirm={() => removeModule(mod.id)} okText="Remove" okType="danger" cancelText="Cancel">
                                <Button type="text" size="small" danger icon={<DeleteOutlined />} style={{ opacity: 0.5 }} />
                            </Popconfirm>
                        </Space>
                    )}
                </div>
                {isOpen && (
                    <>
                        {(mod.assignments || []).map((a, ai) =>
                            renderAssignmentRow(a, ai, 'direct_assignment', mod.id, undefined, 16)
                        )}
                        {(mod.subModules || []).map((sub, si) => renderSubModuleRow(mod, sub, si))}
                    </>
                )}
            </div>
        );
    };

    // ── Edit Panel (right side) ───────────────────────────────────────────────

    const renderBreadcrumb = () => {
        if (!selectedNode) return null;
        const mod = nestedModules.find(m => m.id === selectedNode.moduleId);
        if (!mod) return null;
        const parts: React.ReactNode[] = [
            <Text key="mod" style={{ color: '#1890ff', fontWeight: 600 }}>
                <AppstoreOutlined style={{ marginRight: 4 }} />
                {mod.name || 'Untitled Module'}
            </Text>
        ];
        if (selectedNode.type === 'submodule' || selectedNode.type === 'assignment') {
            const sub = (mod.subModules || []).find(s => s.id === (selectedNode as any).subId);
            if (sub) {
                parts.push(<Text key="sep1" type="secondary" style={{ margin: '0 6px' }}>›</Text>);
                parts.push(
                    <Text key="sub" style={{ color: '#52c41a', fontWeight: 600 }}>
                        <FolderOutlined style={{ marginRight: 4 }} />
                        {sub.name || 'Untitled'}
                    </Text>
                );
                if (selectedNode.type === 'assignment') {
                    const assign = (sub.assignments || []).find(a => a.id === (selectedNode as any).assignId);
                    if (assign) {
                        parts.push(<Text key="sep2" type="secondary" style={{ margin: '0 6px' }}>›</Text>);
                        parts.push(
                            <Text key="assign" style={{ color: '#fa8c16', fontWeight: 600 }}>
                                <FileTextOutlined style={{ marginRight: 4 }} />
                                {assign.title || 'Untitled Assignment'}
                            </Text>
                        );
                    }
                }
            }
        }
        if (selectedNode.type === 'direct_assignment') {
            const assign = (mod.assignments || []).find(a => a.id === (selectedNode as any).assignId);
            if (assign) {
                parts.push(<Text key="sep1" type="secondary" style={{ margin: '0 6px' }}>›</Text>);
                parts.push(
                    <Text key="assign" style={{ color: '#fa8c16', fontWeight: 600 }}>
                        <FileTextOutlined style={{ marginRight: 4 }} />
                        {assign.title || 'Untitled Assignment'}
                    </Text>
                );
            }
        }
        return <div style={{ marginBottom: 16, fontSize: 12, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>{parts}</div>;
    };

    const renderEditPanel = () => {
        if (!selectedNode) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 380, color: '#bfbfbf' }}>
                    <BookOutlined style={{ fontSize: 48, marginBottom: 16, color: '#e8e8e8' }} />
                    <Text type="secondary">Select a module, sub-module, or assignment from the tree to edit it.</Text>
                </div>
            );
        }

        const mod = nestedModules.find(m => m.id === selectedNode.moduleId);
        if (!mod) return null;

        if (selectedNode.type === 'module') {
            return (
                <>
                    {renderBreadcrumb()}
                    <Divider orientation="left" style={{ marginTop: 0 }}>Module Settings</Divider>
                    <Form.Item label="Module Name" required style={{ marginBottom: 16 }}>
                        <Input value={mod.name} onChange={e => updateModuleName(mod.id, e.target.value)}
                            placeholder="e.g. Action Learning" size="large" disabled={!isActive} />
                    </Form.Item>
                    {isActive && (
                        <Space>
                            <Button icon={<FolderOutlined />} onClick={() => addSubModule(mod.id)}>Add Sub-Module</Button>
                            <Button icon={<FileTextOutlined />} onClick={() => addDirectAssignment(mod.id)}>Add Direct Assignment</Button>
                        </Space>
                    )}
                </>
            );
        }

        if (selectedNode.type === 'submodule') {
            const sub = (mod.subModules || []).find(s => s.id === selectedNode.subId);
            if (!sub) return null;
            return (
                <>
                    {renderBreadcrumb()}
                    <Divider orientation="left" style={{ marginTop: 0 }}>Sub-Module Settings</Divider>
                    <Form.Item label="Sub-Module Name" required style={{ marginBottom: 16 }}>
                        <Input value={sub.name} onChange={e => updateSubModuleName(mod.id, sub.id, e.target.value)}
                            placeholder="e.g. Monthly Challenge" size="large" disabled={!isActive} />
                    </Form.Item>
                    {isActive && (
                        <Button icon={<PlusOutlined />} onClick={() => addAssignment(mod.id, sub.id)}>Add Assignment</Button>
                    )}
                    {(sub.assignments || []).length > 0 && (
                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Assignments in this sub-module:</Text>
                            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {sub.assignments.map((a, ai) => (
                                    <Tag key={a.id} color="orange" style={{ cursor: 'pointer' }}
                                        onClick={() => setSelectedNode({ type: 'assignment', moduleId: mod.id, subId: sub.id, assignId: a.id })}>
                                        {a.title || `Assignment ${ai + 1}`}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            );
        }

        if (selectedNode.type === 'assignment') {
            const sub = (mod.subModules || []).find(s => s.id === selectedNode.subId);
            const assign = (sub?.assignments || []).find(a => a.id === selectedNode.assignId);
            if (!assign) return null;
            return (
                <>
                    {renderBreadcrumb()}
                    <Divider orientation="left" style={{ marginTop: 0 }}>Assignment Details</Divider>
                    <AssignmentContent
                        assign={assign}
                        isActive={isActive}
                        pendingFiles={pendingFiles}
                        onUpdate={(field, val) => updateAssignment(mod.id, selectedNode.subId, assign.id, field, val)}
                        onFilesChange={files => setPendingFiles(prev => ({ ...prev, [assign.id]: files }))}
                        onDeleteFile={handleDeleteFile}
                    />
                </>
            );
        }

        if (selectedNode.type === 'direct_assignment') {
            const assign = (mod.assignments || []).find(a => a.id === selectedNode.assignId);
            if (!assign) return null;
            return (
                <>
                    {renderBreadcrumb()}
                    <Divider orientation="left" style={{ marginTop: 0 }}>Assignment Details</Divider>
                    <AssignmentContent
                        assign={assign}
                        isActive={isActive}
                        pendingFiles={pendingFiles}
                        onUpdate={(field, val) => updateDirectAssignment(mod.id, assign.id, field, val)}
                        onFilesChange={files => setPendingFiles(prev => ({ ...prev, [assign.id]: files }))}
                        onDeleteFile={handleDeleteFile}
                    />
                </>
            );
        }

        return null;
    };

    // ── Render ────────────────────────────────────────────────────────────────

    if (fetching) return <div style={{ padding: '100px', textAlign: 'center' }}><Spin size="large" tip="Loading program details..." /></div>;

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin')}>
                    Back to Admin
                </Button>
                <Button
                    icon={<LineChartOutlined />}
                    type="primary"
                    ghost
                    style={{ borderColor: '#fa541c', color: '#fa541c' }}
                    onClick={() => navigate(`/trainer-led-training-details/${encodeURIComponent(trainingData?.trainingId || '')}?mode=analyze`)}
                >
                    Analyze
                </Button>
            </div>

            <Card style={{ marginBottom: 16, borderRadius: 12 }} bodyStyle={{ padding: '12px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 8, backgroundColor: '#fff7e6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isActive ? <EditOutlined style={{ fontSize: 18, color: '#fa541c' }} /> : <EyeOutlined style={{ fontSize: 18, color: '#fa541c' }} />}
                    </div>
                    <Space align="center">
                        <Title level={4} style={{ margin: 0 }}>{isActive ? `Edit — ${trainingData?.trainingName || ''}` : `View — ${trainingData?.trainingName || ''}`}</Title>
                        {!isActive && <Tag color="error">PROGRAM CLOSED</Tag>}
                        {isActive && !isPublished && <Tag color="orange">DRAFT</Tag>}
                    </Space>
                    {isActive && !isPublished && (
                        <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={handlePublish}>
                            Publish Program
                        </Button>
                    )}
                </div>
            </Card>

            <Form form={form} layout="vertical" onFinish={onFinish}>
                {/* ===== SECTION 1: Program Details ===== */}
                <Card title="1. Program Details" style={{ marginBottom: 24, borderRadius: 12 }}>
                    <Row gutter={32}>
                        {/* Left column: program settings */}
                        <Col span={12}>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item name="trainingId" label="Training ID" style={{ marginBottom: 12 }}>
                                        <Input disabled />
                                    </Form.Item>
                                </Col>
                                <Col span={13}>
                                    <Form.Item label="Enrollment Type" name="trainingType" style={{ marginBottom: 12 }}>
                                        <Radio.Group optionType="button" buttonStyle="solid" disabled={!isActive}>
                                            <Radio value={TrainingType.PRIVATE}>Private (Invite Only)</Radio>
                                            <Radio value={TrainingType.OPEN}>Open (Anyone Can Join)</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                                <Col span={5}>
                                    <Form.Item label="Discussion Panel" name="discussionEnabled" valuePropName="checked" style={{ marginBottom: 12 }}>
                                        <Switch checkedChildren="On" unCheckedChildren="Off" disabled={!isActive} />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item label="Program Name" name="trainingName" rules={[{ required: true, message: 'Please enter program name' }]} style={{ marginBottom: 12 }}>
                                        <Input placeholder="e.g. Technology Leadership" size="large" disabled={!isActive} />
                                    </Form.Item>
                                </Col>
                                <Col span={16}>
                                    <Form.Item label="Program Duration (Start - End)" name="dateRange" rules={[{ required: true, message: 'Please select program duration' }]} style={{ marginBottom: 12 }}>
                                        <DatePicker.RangePicker style={{ width: '100%' }} size="large" disabled={!isActive} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Effort (Hours)" name="estimatedEffortHours" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: 12 }}>
                                        <Input type="number" placeholder="e.g. 10" size="large" disabled={!isActive} />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item label="Resource Links (One per line)" name="trainingLink" style={{ marginBottom: 0 }}>
                                        <TextArea rows={3} placeholder="https://docs.microsoft.com..." disabled={!isActive} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>

                        {/* Right column: stakeholder emails */}
                        <Col span={12}>
                            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.trainingType !== curr.trainingType}>
                                {({ getFieldValue }) => {
                                    const isOpen = getFieldValue('trainingType') === TrainingType.OPEN;
                                    return (
                                        <Form.Item label={`Participant Emails (Semicolon separated)${isOpen ? ' — optional for Open programs' : ''}`} name="audienceEmails" rules={isOpen ? [] : [{ required: true }]} style={{ marginBottom: 12 }}>
                                            <TextArea rows={3} placeholder="user1@company.com; user2@company.com" disabled={!isActive} />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                            <Form.Item label="Stakeholder Emails (Semicolon separated)" name="stakeholderEmails" rules={[{ required: true }]} style={{ marginBottom: 12 }}>
                                <TextArea rows={3} placeholder="manager1@company.com" disabled={!isActive} />
                            </Form.Item>
                            <Form.Item label="Trainer Emails (Semicolon separated)" name="trainerEmails" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                <TextArea rows={3} placeholder="trainer1@company.com" disabled={!isActive} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* ===== SECTION 2: Two-Panel Modules & Curriculum ===== */}
                <div style={{ marginBottom: 24 }}>
                    <Title level={4} style={{ margin: '0 0 16px 0' }}>
                        2. Modules & Curriculum <span style={{ color: '#ff4d4f' }}>*</span>
                    </Title>

                    {nestedModules.length === 0 ? (
                        <Card style={{ textAlign: 'center', padding: 40, backgroundColor: '#fafafa', borderStyle: 'dashed', borderRadius: 12 }}>
                            <BookOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                            <p style={{ color: '#8c8c8c' }}>No modules added yet. Click "Add Module" to get started.</p>
                        </Card>
                    ) : (
                        <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', height: 600 }}>
                            {/* Left: Tree Panel */}
                            <Card
                                style={{ width: 320, flexShrink: 0, borderRadius: 12, display: 'flex', flexDirection: 'column' }}
                                bodyStyle={{ padding: '8px 6px', overflowY: 'auto', flex: 1 }}
                                title={
                                    <span style={{ fontSize: 13, color: '#595959' }}>
                                        <AppstoreOutlined style={{ marginRight: 6 }} />
                                        Program Structure
                                    </span>
                                }
                                extra={isActive && (
                                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addModule}>
                                        Add Module
                                    </Button>
                                )}
                                size="small"
                            >
                                <div ref={treeBodyRef}>
                                    {nestedModules.map((mod, idx) => renderModuleRow(mod, idx))}
                                </div>
                            </Card>

                            {/* Right: Edit Panel */}
                            <Card
                                style={{ flex: 1, borderRadius: 12, display: 'flex', flexDirection: 'column' }}
                                bodyStyle={{ padding: 24, flex: 1, overflowY: 'auto' }}
                            >
                                {renderEditPanel()}
                            </Card>
                        </div>
                    )}
                </div>

                <div style={{ textAlign: 'right', marginTop: 32, marginBottom: 50 }}>
                    <Space size="large">
                        {(!isActive || !isPublished) && (
                            <Button danger size="large" icon={<DeleteOutlined />}
                                onClick={() => { setDeleteConfirmationText(''); setIsDeleteModalVisible(true); }}>
                                Delete Program
                            </Button>
                        )}
                        <Button onClick={() => navigate('/admin')} size="large">{isActive ? 'Cancel' : 'Back to Admin'}</Button>
                        {isActive && (
                            <>
                                {isPublished && (
                                    <Button danger size="large" icon={<StopOutlined />} onClick={handleCloseProgram}>Close Program</Button>
                                )}
                                <Tooltip title={notifyParticipant ? 'Participants will be notified by email on save' : 'Participants will NOT be notified on save'}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: notifyParticipant ? '#f0f5ff' : '#fafafa', borderRadius: 6, border: `1px solid ${notifyParticipant ? '#adc6ff' : '#d9d9d9'}`, cursor: 'default' }}>
                                        <BellOutlined style={{ color: notifyParticipant ? '#1890ff' : '#bfbfbf', fontSize: 14 }} />
                                        <Text style={{ fontSize: 13, color: notifyParticipant ? '#1890ff' : '#8c8c8c', whiteSpace: 'nowrap' }}>Notify Participants</Text>
                                        <Switch size="small" checked={notifyParticipant} onChange={setNotifyParticipant} />
                                    </div>
                                </Tooltip>
                                <Button type="primary" size="large" icon={<SaveOutlined />} loading={loading}
                                    onClick={() => form.submit()}
                                    style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}>
                                    Save Changes
                                </Button>
                            </>
                        )}
                    </Space>
                </div>
            </Form>

            {/* Delete Confirmation Modal */}
            <Modal
                title={<span style={{ color: 'red' }}>Delete Program: {trainingData?.trainingName}</span>}
                open={isDeleteModalVisible}
                onCancel={() => { setIsDeleteModalVisible(false); setDeleteConfirmationText(''); }}
                onOk={handleDeleteTraining}
                okText="Permanently Delete"
                okButtonProps={{ danger: true, disabled: deleteConfirmationText.trim() !== trainingData?.trainingName.trim(), loading }}
                centered
            >
                <Alert message="Warning: This action cannot be undone." type="warning" showIcon style={{ marginBottom: 16 }} />
                <Text style={{ display: 'block', marginBottom: 8 }}>
                    Please type <strong>{trainingData?.trainingName}</strong> to confirm:
                </Text>
                <Input value={deleteConfirmationText} onChange={e => setDeleteConfirmationText(e.target.value)}
                    placeholder="Type program name exactly" maxLength={250} />
            </Modal>

            {/* Progress Modal */}
            <Modal
                title={<Space>{processStatus === 'active' && <LoadingOutlined />}<span>{processStatus === 'active' ? 'Saving Program...' : 'Update Complete'}</span></Space>}
                open={progressModalVisible}
                closable={processStatus === 'exception'}
                maskClosable={processStatus === 'exception'}
                onCancel={() => setProgressModalVisible(false)}
                footer={processStatus === 'exception' ? [<Button key="close" onClick={() => setProgressModalVisible(false)}>Close</Button>] : null}
                centered
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Progress type="circle" percent={percent} status={processStatus}
                        strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }} />
                    <div style={{ marginTop: 24 }}>
                        <Text strong style={{ fontSize: 16 }}>{progressMessage}</Text>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default EditTrainerLedProgram;

// ===== Assignment Content (renders form fields without Card wrapper) =====
interface AssignmentContentProps {
    assign: NestedAssignment;
    isActive: boolean;
    pendingFiles: Record<string, any[]>;
    onUpdate: (field: keyof NestedAssignment, value: any) => void;
    onFilesChange: (files: any[]) => void;
    onDeleteFile: (fileId: number, assignmentId?: number | string) => void;
}

const AssignmentContent: React.FC<AssignmentContentProps> = ({
    assign, isActive, pendingFiles, onUpdate, onFilesChange, onDeleteFile
}) => (
    <>
        <Row gutter={16}>
            <Col span={24}>
                <Form.Item label="Assignment Title" required style={{ marginBottom: 12 }}>
                    <Input value={assign.title} onChange={(e) => onUpdate('title', e.target.value)}
                        placeholder="e.g. Propose High Level Challenge" size="large" disabled={!isActive} />
                </Form.Item>
            </Col>
            <Col span={24}>
                <Form.Item label="Description / Instructions" required style={{ marginBottom: 12 }}>
                    <Input.TextArea rows={3} value={assign.description}
                        onChange={(e) => onUpdate('description', e.target.value)}
                        placeholder="Describe the assignment, expectations, and deliverables..." disabled={!isActive} />
                </Form.Item>
            </Col>
            {assign.workflowEnabled ? (
                <>
                    <Col span={8}>
                        <Form.Item label="Proposal Deadline" required style={{ marginBottom: 12 }}>
                            <DatePicker showTime={{ defaultValue: dayjs('23:59:59', 'HH:mm:ss') }} style={{ width: '100%' }}
                                value={assign.deadline ? dayjs(assign.deadline) : null}
                                onChange={(val) => onUpdate('deadline', val)} size="large" disabled={!isActive} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Max Credits" required style={{ marginBottom: 12 }}>
                            <InputNumber min={0} max={1000} value={assign.maxCredits}
                                onChange={(val) => onUpdate('maxCredits', val ?? 0)}
                                style={{ width: '100%' }} size="large" disabled={!isActive} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Implementation Deadline" style={{ marginBottom: 12 }}>
                            <DatePicker showTime={{ defaultValue: dayjs('23:59:59', 'HH:mm:ss') }} style={{ width: '100%' }}
                                value={assign.stateDeadlines?.completed ? dayjs.unix(assign.stateDeadlines.completed as number) : null}
                                onChange={(val) => onUpdate('stateDeadlines', { ...(assign.stateDeadlines || {}), completed: val ? val.unix() : null })}
                                size="large" disabled={!isActive} />
                        </Form.Item>
                    </Col>
                </>
            ) : (
                <>
                    <Col span={8}>
                        <Form.Item label="Max Credits" required style={{ marginBottom: 12 }}>
                            <InputNumber min={0} max={1000} value={assign.maxCredits}
                                onChange={(val) => onUpdate('maxCredits', val ?? 0)}
                                style={{ width: '100%' }} size="large" disabled={!isActive} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Final Deadline" style={{ marginBottom: 12 }}>
                            <DatePicker showTime={{ defaultValue: dayjs('23:59:59', 'HH:mm:ss') }} style={{ width: '100%' }}
                                value={assign.stateDeadlines?.completed ? dayjs.unix(assign.stateDeadlines.completed as number) : null}
                                onChange={(val) => onUpdate('stateDeadlines', { ...(assign.stateDeadlines || {}), completed: val ? val.unix() : null })}
                                size="large" disabled={!isActive} />
                        </Form.Item>
                    </Col>
                </>
            )}
            <Col span={24}>
                <Form.Item style={{ marginBottom: 8 }}>
                    <Checkbox
                        checked={!!assign.workflowEnabled}
                        onChange={(e) => onUpdate('workflowEnabled', e.target.checked)}
                        disabled={!isActive}
                    >
                        Enable workflow status (proposal → review → approval cycle)
                    </Checkbox>
                </Form.Item>
            </Col>
            <Col span={24}>
                <Form.Item label="Evaluation Criteria" style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {(assign.evalCriteria || []).map((c, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Input
                                    placeholder={`Criterion ${i + 1} name`}
                                    value={c.name}
                                    disabled={!isActive}
                                    onChange={e => {
                                        const updated = (assign.evalCriteria || []).map((x, j) => j === i ? { ...x, name: e.target.value } : x);
                                        onUpdate('evalCriteria', updated);
                                    }}
                                    style={{ flex: 1 }}
                                />
                                {isActive && (
                                    <Button
                                        type="text" size="small" danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => onUpdate('evalCriteria', (assign.evalCriteria || []).filter((_, j) => j !== i))}
                                    />
                                )}
                            </div>
                        ))}
                        {isActive && (
                            <Button
                                type="dashed" size="small" icon={<PlusOutlined />}
                                onClick={() => onUpdate('evalCriteria', [...(assign.evalCriteria || []), { name: '' }])}
                                style={{ alignSelf: 'flex-start' }}
                            >
                                Add Criterion
                            </Button>
                        )}
                    </div>
                </Form.Item>
            </Col>
        </Row>

        {assign.introfiles && assign.introfiles.length > 0 && (
            <div style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}><PaperClipOutlined /> Existing Reference Files:</Text>
                <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {assign.introfiles.map((file: any) => (
                        <div key={file.id} style={{ display: 'flex', alignItems: 'center', background: '#fff', padding: '2px 8px', borderRadius: 4, border: '1px solid #d9d9d9' }}>
                            <Text style={{ fontSize: 12, marginRight: 8 }}>{file.filename}</Text>
                            <Button type="link" size="small" icon={<DownloadOutlined />} href={file.bloburl} target="_blank" />
                            {isActive && (
                                <Popconfirm title="Remove this file?" onConfirm={() => onDeleteFile(file.id, assign.moodleId)} okText="Delete" okType="danger" cancelText="Cancel">
                                    <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        )}

        <div style={{ padding: 12, background: '#f9f9f9', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary"><PaperClipOutlined /> Reference Materials</Text>
                {isActive && (
                    <Upload fileList={pendingFiles[assign.id] || []}
                        onChange={({ fileList }) => onFilesChange(fileList)}
                        beforeUpload={() => false} multiple>
                        <Button icon={<PlusOutlined />} size="small">Attach Files</Button>
                    </Upload>
                )}
            </div>
            {(pendingFiles[assign.id]?.length > 0) && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    {pendingFiles[assign.id].length} file(s) queued for upload
                </div>
            )}
        </div>
    </>
);
